// components/profile/Dashboard.jsx
import React from "react";
import { Map, Route, Award } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import ShimmerLoader from "../common/ShimmerLoader";

const Dashboard = () => {
  const { user, stats, isLoading } = useAuth();

  if (isLoading) {
    return (
      <section className="space-y-8">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            Dashboard
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Loading your stats...
          </p>
        </div>
        <ShimmerLoader count={4} />
      </section>
    );
  }

  const cards = [
    {
      id: "totalDistance",
      title: "Total Distance",
      value: `${stats?.total_distance ?? 0}`,
      unit: "km",
      hint: `${stats?.total_rides ?? 0} total rides`,
      icon: <Map className="w-6 h-6" />,
      isHero: true,
    },
    {
      id: "achievements",
      title: "Achievements",
      value: stats?.achievements ?? 0,
      unit: "",
      hint: "badges earned",
      icon: <Award className="w-5 h-5" />,
      isHero: true,
    },
    {
      id: "kosetDistance",
      title: "Koset Distance",
      value: `${stats?.koset_distance ?? 0}`,
      unit: "km",
      hint: `${stats?.koset_rides ?? 0} koset rides`,
      icon: <Route className="w-5 h-5" />,
    },
    {
      id: "Activity Distance",
      title: "Activity Distance",
      value: `${stats?.activity_distance ?? 0}`,
      unit: "km",
      hint: `${stats?.activity_rides ?? 0} activity rides`,
      icon: <Route className="w-5 h-5" />,
    },
  ];

  return (
    <section className="space-y-8">
      {/* Title */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Dashboard
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Plan, prioritize, and accomplish your cycling goals.
          </p>
        </div>
      </div>

      {/* Stat Cards - Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((card) => (
          <DashboardCard key={card.id} {...card} />
        ))}
      </div>
    </section>
  );
};

const DashboardCard = ({ title, value, unit, hint, icon }) => {
  return (
    <div className="rounded-3xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 overflow-hidden border border-gray-100 dark:border-gray-700 flex flex-col min-h-[180px]">
      {/* Gradient Header */}
      <div className="bg-gradient-to-br from-[#1e8a3b] to-[#114a1f] dark:from-[#166534] dark:to-[#052e16] px-6 pt-5 pb-4">
        <h3 className="text-green-50 font-medium text-sm drop-shadow-sm">
          {title}
        </h3>
        <div className="mt-2 flex items-baseline gap-1">
          <span className="text-4xl font-bold text-white drop-shadow-sm">
            {value}
          </span>
          {unit && <span className="text-lg text-green-100/90">{unit}</span>}
        </div>
      </div>

      {/* Glossy White Body */}
      <div className="bg-gradient-to-b from-white via-white to-gray-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 px-5 py-2.5 flex items-end">
        <div className="flex items-center gap-2 text-sm bg-gray-100/80 dark:bg-gray-700/80 w-fit px-4 py-2 rounded-full backdrop-blur-md border border-gray-200/50 dark:border-gray-600/50 shadow-sm">
          <div className="bg-white dark:bg-gray-600 w-6 h-6 rounded-full flex items-center justify-center text-green-600 dark:text-green-400 shadow-sm">
            {React.cloneElement(icon, { className: "w-3.5 h-3.5" })}
          </div>
          <span className="text-gray-600 dark:text-gray-300 font-medium">
            {hint}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
